<script setup lang="ts">
import DSContainer from '~/components/ds/Container.vue'

const {
  t, currentStep, totalSteps, isLoading, initialized,
  canProceed, stepLabels, nextStep, prevStep, focusWizard, initWizard, resetWizard,
  localePath, formatPrice,
  selectedServiceIds, selectedServices, totalPrice, durationRange, maxWarranty,
  isLoadingRepairCategories,
} = useRepairWizard()

definePageMeta({
  layout: 'default'
})

useSeoMeta({
  title: () => t('seo.repair.title'),
  description: () => t('seo.repair.description'),
})

useSeoCanonical('/restauracion')

// Process steps for How It Works section
const processSteps = computed(() => [
  { number: 1, titleKey: 'repairPage.process.step1.title', descKey: 'repairPage.process.step1.desc', icon: 'i-lucide-smartphone' },
  { number: 2, titleKey: 'repairPage.process.step2.title', descKey: 'repairPage.process.step2.desc', icon: 'i-lucide-clipboard-list' },
  { number: 3, titleKey: 'repairPage.process.step3.title', descKey: 'repairPage.process.step3.desc', icon: 'i-lucide-wrench' },
  { number: 4, titleKey: 'repairPage.process.step4.title', descKey: 'repairPage.process.step4.desc', icon: 'i-lucide-check-circle' },
])

// FAQ items
const faqItems = computed(() => [
  { questionKey: 'repairPage.faq.q1.question', answerKey: 'repairPage.faq.q1.answer' },
  { questionKey: 'repairPage.faq.q2.question', answerKey: 'repairPage.faq.q2.answer' },
  { questionKey: 'repairPage.faq.q3.question', answerKey: 'repairPage.faq.q3.answer' },
  { questionKey: 'repairPage.faq.q4.question', answerKey: 'repairPage.faq.q4.answer' },
])

const openFaq = ref<number | null>(null)
const toggleFaq = (index: number) => {
  openFaq.value = openFaq.value === index ? null : index
}

const repairTrustBadges = computed(() => [
  { icon: 'i-lucide-shield-check', label: t('repairPage.trustBadges.warranty') },
  { icon: 'i-lucide-clock-3', label: t('repairPage.trustBadges.fastService') },
  { icon: 'i-lucide-award', label: t('repairPage.trustBadges.originalParts') },
])

onMounted(() => {
  initWizard()
})

async function handleNextStep() {
  await nextStep()
  await focusWizard()
}

async function handlePreviousStep() {
  await prevStep()
  await focusWizard()
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950">
    <DSContainer class="pt-5">
      <PageHeroBanner
        theme="repair"
        variant="service"
        :home-label="t('repairPage.breadcrumb.home')"
        :current-label="t('repairPage.breadcrumb.repair')"
        :home-to="localePath('/')"
        :badge="t('repairPage.badge')"
        :title="t('repairPage.title')"
        :title-highlight="t('repairPage.titleHighlight')"
        :subtitle="t('repairPage.subtitle')"
        hero-icon="i-lucide-wrench"
        visual-src="/images/heroes/repair-workbench.webp"
        :cta-label="t('repairPage.finalCta.cta')"
        cta-to="#repair-wizard"
        :trust-badges="repairTrustBadges"
      />
    </DSContainer>

    <DSContainer class="py-12">
      <!-- Repair Wizard -->
      <div id="repair-wizard" class="w-full mb-16">
        <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800">
          <!-- Progress Bar -->
          <div id="repair-wizard-progress" tabindex="-1" class="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 border-b border-gray-100 dark:border-gray-800 focus:outline-none">
            <div class="flex items-center justify-between mb-2">
              <h2 class="font-semibold text-gray-900 dark:text-white">{{ t('repairPage.calculator.title') }}</h2>
              <span class="text-sm text-gray-500 dark:text-gray-400">
                {{ t('repairPage.wizard.stepOf', { current: currentStep, total: totalSteps }) }}
              </span>
            </div>
            <div
              class="flex gap-2"
              role="progressbar"
              :aria-label="t('repairPage.calculator.title')"
              :aria-valuemin="1"
              :aria-valuemax="totalSteps"
              :aria-valuenow="currentStep"
              :aria-valuetext="`${t('repairPage.wizard.stepOf', { current: currentStep, total: totalSteps })}: ${stepLabels[currentStep - 1]}`"
            >
              <div
                v-for="step in totalSteps"
                :key="step"
                aria-hidden="true"
                class="flex-1 h-2 rounded-full transition-colors"
                :class="step <= currentStep ? 'bg-brand-500' : 'bg-gray-200 dark:bg-gray-700'"
              />
            </div>
            <span class="sr-only" aria-live="polite" aria-atomic="true">
              {{ t('repairPage.wizard.stepOf', { current: currentStep, total: totalSteps }) }}: {{ stepLabels[currentStep - 1] }}
            </span>
          </div>

          <div class="p-6 sm:p-8">
            <!-- Loading Overlay (initial load only) -->
            <div v-if="!initialized" class="flex items-center justify-center py-16">
              <div class="w-8 h-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
            </div>

            <!-- Child Step Pages -->
            <NuxtPage v-else />
          </div>

          <!-- Combined sticky bottom: price summary (step 2) + nav buttons. Stacked as one
               sticky container so they don't overlap on narrow viewports. No overflow-hidden
               on the wrapper so the upward shadow on children can bleed over scroll content. -->
          <div v-if="initialized && !isLoading && currentStep <= totalSteps" class="sticky bottom-0 z-10">
            <!-- Step 2 price summary -->
            <div v-if="currentStep === 2 && !isLoadingRepairCategories && selectedServices.length > 0" class="bg-gradient-to-r from-gray-50 to-brand-50 dark:from-gray-800 dark:to-brand-900/20 border-t-2 border-brand-200 dark:border-brand-800 px-4 py-3 sm:px-8 sm:py-5 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]">
              <div class="flex items-center justify-between gap-3">
                <div class="flex min-w-0 items-center gap-2 sm:gap-4">
                  <div class="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800 sm:px-4">
                    <div class="truncate text-xs font-medium text-gray-700 dark:text-gray-300">{{ t('repairPage.wizard.servicesSelected', { count: selectedServices.length }) }}</div>
                  </div>
                  <span class="hidden text-xl font-light text-gray-400 sm:inline">=</span>
                  <div class="shrink-0 rounded-lg bg-brand-600 px-4 py-2 shadow-lg dark:bg-brand-700 sm:px-5">
                    <div class="text-xs text-brand-100">{{ t('repairPage.totalPrice') }}</div>
                    <div class="text-xl font-bold text-white sm:text-2xl">{{ formatPrice(totalPrice) }}</div>
                  </div>
                </div>
                <div class="hidden flex-wrap items-center gap-x-4 gap-y-2 text-sm sm:flex">
                  <div class="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <UIcon name="i-lucide-clock" class="w-4 h-4" />
                    <span>{{ t('repairPage.wizard.estimatedDuration') }}: {{ t('repairPage.durationDays', durationRange) }}</span>
                  </div>
                  <div class="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <UIcon name="i-lucide-shield-check" class="w-4 h-4" />
                    <span>{{ t('repairPage.wizard.warrantyIncluded') }}: {{ t('repairPage.warrantyMonths', { months: maxWarranty }) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Nav buttons (steps 1-3). rounded-b-2xl lives on the last child so the
                 card's bottom corners stay rounded without needing overflow-hidden on the
                 wrapper (which would clip the upward shadow). When the price panel is
                 visible, nav sits below it; its shadow is inherited from the price panel. -->
            <div v-if="currentStep < 4" class="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 px-6 sm:px-8 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-4 rounded-b-2xl flex items-center justify-between" :class="{ 'shadow-[0_-8px_30px_rgba(0,0,0,0.12)]': !(currentStep === 2 && !isLoadingRepairCategories && selectedServices.length > 0) }">
              <button
                v-if="currentStep > 1"
                class="-ml-3 flex min-h-11 items-center gap-2 rounded-lg px-3 text-gray-600 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:text-gray-400 dark:hover:text-white dark:focus-visible:ring-offset-gray-900"
                @click="handlePreviousStep"
              >
                <UIcon name="i-lucide-arrow-left" class="w-5 h-5" />
                {{ t('repairPage.wizard.back') }}
              </button>
              <div v-else />

              <button
                :disabled="!canProceed"
                class="flex min-h-11 items-center gap-2 rounded-xl bg-brand-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:focus-visible:ring-offset-gray-900"
                @click="handleNextStep"
              >
                {{ t('repairPage.wizard.continue') }}
                <UIcon name="i-lucide-arrow-right" class="w-5 h-5" />
              </button>
            </div>

            <!-- Step 4 back button only -->
            <div v-else class="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 px-6 sm:px-8 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-4 rounded-b-2xl flex items-center shadow-[0_-8px_30px_rgba(0,0,0,0.12)]">
              <button
                class="-ml-3 flex min-h-11 items-center gap-2 rounded-lg px-3 text-gray-600 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:text-gray-400 dark:hover:text-white dark:focus-visible:ring-offset-gray-900"
                @click="handlePreviousStep"
              >
                <UIcon name="i-lucide-arrow-left" class="w-5 h-5" />
                {{ t('repairPage.wizard.back') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Trust indicators -->
        <div class="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 dark:text-gray-400">
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-users" class="w-4 h-4 text-brand-600" />
            <span>{{ t('repairPage.calculator.stats.customers') }}</span>
          </div>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-star" class="w-4 h-4 text-brand-600" />
            <span>{{ t('repairPage.calculator.stats.rating') }}</span>
          </div>
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-clock" class="w-4 h-4 text-brand-600" />
            <span>{{ t('repairPage.calculator.stats.avgTime') }}</span>
          </div>
        </div>
      </div>

      <!-- How It Works Section -->
      <div class="mb-16">
        <div class="text-center mb-10">
          <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3">{{ t('repairPage.howItWorks.title') }}</h2>
          <p class="text-gray-600 dark:text-gray-400">{{ t('repairPage.howItWorks.subtitle') }}</p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          <div v-for="(step, index) in processSteps" :key="step.number" class="relative">
            <div class="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-md border border-gray-100 dark:border-gray-800 h-full flex flex-col">
              <div class="flex items-center justify-between mb-4">
                <div class="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center font-bold text-white text-sm">{{ step.number }}</div>
                <UIcon v-if="index < processSteps.length - 1" name="i-lucide-arrow-right" class="w-5 h-5 hidden lg:block text-brand-600" />
              </div>
              <div class="w-14 h-14 rounded-xl bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center mb-4">
                <UIcon :name="step.icon" class="w-7 h-7 text-brand-600 dark:text-brand-400" />
              </div>
              <h3 class="font-bold text-gray-900 dark:text-white mb-2 text-lg">{{ t(step.titleKey) }}</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400 flex-grow">{{ t(step.descKey) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Why Choose Us Section -->
      <div class="mb-16">
        <div class="text-center mb-10">
          <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3">{{ t('repairPage.whyChoose.title') }}</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div class="bg-white dark:bg-gray-900 rounded-2xl p-6 text-center border border-gray-100 dark:border-gray-800">
            <div class="w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center mx-auto mb-4">
              <UIcon name="i-lucide-shield-check" class="w-8 h-8 text-brand-600 dark:text-brand-400" />
            </div>
            <h3 class="font-bold text-gray-900 dark:text-white mb-2">{{ t('repairPage.whyChoose.benefit1.title') }}</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">{{ t('repairPage.whyChoose.benefit1.desc') }}</p>
          </div>
          <div class="bg-white dark:bg-gray-900 rounded-2xl p-6 text-center border border-gray-100 dark:border-gray-800">
            <div class="w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center mx-auto mb-4">
              <UIcon name="i-lucide-award" class="w-8 h-8 text-brand-600 dark:text-brand-400" />
            </div>
            <h3 class="font-bold text-gray-900 dark:text-white mb-2">{{ t('repairPage.whyChoose.benefit2.title') }}</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">{{ t('repairPage.whyChoose.benefit2.desc') }}</p>
          </div>
          <div class="bg-white dark:bg-gray-900 rounded-2xl p-6 text-center border border-gray-100 dark:border-gray-800">
            <div class="w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center mx-auto mb-4">
              <UIcon name="i-lucide-zap" class="w-8 h-8 text-brand-600 dark:text-brand-400" />
            </div>
            <h3 class="font-bold text-gray-900 dark:text-white mb-2">{{ t('repairPage.whyChoose.benefit3.title') }}</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">{{ t('repairPage.whyChoose.benefit3.desc') }}</p>
          </div>
        </div>
      </div>

      <!-- FAQ Section -->
      <div class="max-w-3xl mx-auto mb-16">
        <div class="text-center mb-10">
          <h2 class="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3">{{ t('repairPage.faq.title') }}</h2>
        </div>
        <div class="space-y-3">
          <div v-for="(item, index) in faqItems" :key="index" class="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
            <button class="w-full px-6 py-4 flex items-center justify-between text-left" @click="toggleFaq(index)">
              <span class="font-semibold text-gray-900 dark:text-white">{{ t(item.questionKey) }}</span>
              <UIcon :name="openFaq === index ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'" class="w-5 h-5 text-gray-500 flex-shrink-0" />
            </button>
            <Transition name="accordion">
              <div v-if="openFaq === index" class="px-6 pb-4">
                <p class="text-gray-600 dark:text-gray-400 text-sm">{{ t(item.answerKey) }}</p>
              </div>
            </Transition>
          </div>
        </div>
      </div>

      <!-- Final CTA -->
      <div class="bg-gradient-to-br from-brand-600 to-indigo-700 dark:from-brand-800 dark:to-indigo-900 rounded-2xl p-8 sm:p-12 text-center">
        <h2 class="text-2xl sm:text-3xl font-bold text-white mb-4">{{ t('repairPage.finalCta.title') }}</h2>
        <p class="text-brand-100 mb-6 max-w-2xl mx-auto">{{ t('repairPage.finalCta.subtitle') }}</p>
        <button
          class="inline-flex items-center gap-2 bg-white text-brand-700 hover:bg-brand-50 px-8 py-4 rounded-xl font-semibold text-lg transition-colors"
          @click="resetWizard()"
        >
          {{ t('repairPage.finalCta.cta') }}
          <UIcon name="i-lucide-arrow-up" class="w-5 h-5" />
        </button>
      </div>
    </DSContainer>
  </div>
</template>

<style scoped>
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}
.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  max-height: 0;
}
.accordion-enter-to,
.accordion-leave-from {
  max-height: 200px;
}
</style>
